import { Tag } from '@ads/components-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { CreatePersonRequestViewModel, EGenderEnum } from '@/shared/api/generated/types';
import { Alert } from '@/shared/ui/atoms';
import type { Person } from '../../../types/person';
import { usePersonFormNavigation } from './hooks/usePersonFormNavigation';
import { usePersonFormSync } from './hooks/usePersonFormSync';
import { PersonFormActionBar } from './PersonFormActionBar';
import { PersonFormHeader } from './PersonFormHeader';
import { PersonFormStepper } from './PersonFormStepper';
import { PersonFormTabs } from './PersonFormTabs';
import type { StepKey } from './personFormConfig';
import { BASE_STEP_KEYS, EDIT_STEP_KEYS } from './personFormConfig';
import { countErrorsByStep, getPersonFormDefaultValues } from './personFormHelpers';
import { type PersonFormValues, usePersonFormSchema } from './personFormSchema';
import { AdditionalInfoTab } from './tabs/AdditionalInfoTab';
import { AddressesTab } from './tabs/AddressesTab';
import { DocumentsTab } from './tabs/DocumentsTab';
import { FinancialTab } from './tabs/FinancialTab';
import { PersonalTab } from './tabs/PersonalTab';
import { ProfessionalTab } from './tabs/ProfessionalTab';
import { ProfilesTab } from './tabs/ProfilesTab';
import { RolesTab } from './tabs/RolesTab';
import { UnsavedChangesModal } from './UnsavedChangesModal';

export type { StepKey };

// ─── Props ────────────────────────────────────────────────────────────────────

export interface PersonFormProps {
  readonly person?: Person;
  readonly onSubmit: (data: CreatePersonRequestViewModel) => void;
  /**
   * Callback alternativo usado em modo edição. Recebe os valores brutos do
   * formulário e o objeto `dirtyFields` do react-hook-form, permitindo que o
   * consumidor dispare apenas os endpoints PUT correspondentes às seções
   * modificadas. Quando ausente, modo edição cai no `onSubmit`.
   */
  readonly onUpdateByStep?: (params: {
    readonly values: PersonFormValues;
    readonly dirtyFields: Record<string, unknown>;
  }) => void;
  /**
   * Callback chamado ao avançar ou trocar de aba em modo edição.
   * Recebe a chave da etapa atual para que o consumidor dispare apenas
   * o endpoint correspondente àquela seção.
   */
  readonly onSaveStep?: (params: {
    readonly step: string;
    readonly values: PersonFormValues;
    readonly dirtyFields: Record<string, unknown>;
  }) => Promise<void>;
  readonly onCancel: () => void;
  readonly isSubmitting?: boolean;
  /** Mensagem de erro retornada pelo servidor (ex: CPF já cadastrado). Exibida como Alert. */
  readonly serverError?: string | null;
  /** Loading do step Perfis (student/employee/teacher): controla skeleton e sincronização de campos */
  readonly isLoadingProfiles?: boolean;
  /** Loading do step Financeiro: controla skeleton e sincronização de campos */
  readonly isLoadingFinancial?: boolean;
  /** Aba aberta inicialmente em modo edição. Padrão: `'personal'` */
  readonly initialStep?: StepKey | undefined;
}

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Formulário completo de criação e edição de pessoa.
 *
 * Organiza o cadastro em um wizard de 7 etapas (Pessoal, Contato, Profissional,
 * Documentos, Endereços, Perfis e Acesso, Financeiro), com Stepper de navegação,
 * resumo de validação contextual e barra de ações fixa.
 *
 * @param person - Dados para pré-popular o formulário (modo edição)
 * @param onSubmit - Callback com payload da API após validação
 * @param onCancel - Callback ao cancelar
 * @param isSubmitting - Indica operação em andamento
 */
export function PersonForm({
  person,
  onSubmit,
  onUpdateByStep,
  onSaveStep,
  onCancel,
  isSubmitting = false,
  isLoadingProfiles = false,
  isLoadingFinancial = false,
  initialStep,
  serverError,
}: PersonFormProps) {
  const { t } = useTranslation('personManagement');

  const StepKeys = person ? EDIT_STEP_KEYS : BASE_STEP_KEYS;

  const [rolesIsDirty, setRolesIsDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const rolesSaveRef = useRef<(() => Promise<void>) | undefined>(undefined);

  const personFormSchema = usePersonFormSchema();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    reset,
    trigger,
    formState: { errors, isSubmitted, isDirty, dirtyFields },
  } = useForm<PersonFormValues>({
    resolver: zodResolver(personFormSchema),
    defaultValues: getPersonFormDefaultValues(person),
  });

  usePersonFormSync({ person, isLoadingProfiles, isLoadingFinancial, setValue });

  const {
    currentStep,
    maxReachedStep,
    stepSaveStatus,
    stepSaveError,
    goToStep,
    handleNext,
    handlePrevious,
    handleSaveRolesStep,
  } = usePersonFormNavigation({
    person,
    stepKeys: StepKeys,
    initialStep,
    trigger,
    dirtyFields: dirtyFields as Record<string, unknown>,
    getValues,
    reset,
    onSaveStep,
    rolesSaveRef,
  });

  const onFormSubmit = async (values: PersonFormValues) => {
    if (person && onUpdateByStep) {
      onUpdateByStep({
        values,
        dirtyFields: dirtyFields as Record<string, unknown>,
      });
      return;
    }
    const mapAddress = (addr: typeof values.personalAddress) => {
      if (!addr) return undefined;
      const hasMeaningfulValue =
        addr.street || addr.zipCode || addr.placeNumber || addr.neighborhood || addr.cityId;
      if (!hasMeaningfulValue) return undefined;
      return {
        street: addr.street ?? null,
        zipCode: addr.zipCode ?? null,
        placeNumber: addr.placeNumber ?? null,
        neighborhood: addr.neighborhood ?? null,
        complement: addr.complement ?? null,
        countryId: addr.countryId ?? 1,
        stateId: addr.stateId ?? null,
        cityId: addr.cityId ?? null,
        stateName: addr.stateName ?? null,
        cityName: addr.cityName ?? null,
      };
    };

    onSubmit({
      firstName: values.firstName,
      lastName: values.lastName,
      alias: values.alias ?? null,
      email: values.email,
      cpf: values.cpf.replace(/\D/g, ''),
      celPhone: values.celPhone,
      homePhone: values.homePhone ?? null,
      workPhone: values.workPhone ?? null,
      birthDate: values.birthDate ?? null,
      gender: values.gender as EGenderEnum | undefined,
      martialStatus: values.martialStatus,
      nationalityId: values.nationalityId,
      birthCountryId: values.birthCountryId,
      birthCountryName: null,
      birthStateId: values.birthStateId,
      birthStateName: values.birthStateName ?? null,
      birthCityId: values.birthCityId,
      birthCityName: values.birthCityName ?? null,
      mothersName: values.mothersName ?? null,
      fathersName: values.fathersName ?? null,
      spouseName: values.spouseName ?? null,
      receiveSms: values.receiveSms,
      receiveMarketing: values.receiveMarketing,
      miniCurriculum: values.miniCurriculum ?? null,
      urlLinkedin: values.urlLinkedin ?? null,
      urlLattes: values.urlLattes ?? null,
      observation: values.observation ?? null,
      rg: values.rg ?? null,
      dispatcherOrgan: values.dispatcherOrgan ?? null,
      dispatcherOrganState: values.dispatcherOrganState ?? null,
      dateDispatch: values.dateDispatch ?? null,
      passportNumber: values.passportNumber ?? null,
      idCountryNumber: values.idCountryNumber ?? null,
      profession: values.profession ?? null,
      professionId: values.professionId,
      company: values.company ?? null,
      associatedCompanyId: values.associatedCompanyId,
      jobPositionId: values.jobPositionId,
      otherJobPosition: values.otherJobPosition ?? null,
      businessSectorId: values.businessSectorId,
      otherBusinessSector: values.otherBusinessSector ?? null,
      serviceTime: values.serviceTime,
      experienceLevel: values.experienceLevel,
      selfEmployed: values.selfEmployed,
      isWorking: values.isWorking,
      academicEducation: values.academicEducation,
      academicInstitutionId: values.academicInstitutionId,
      academicCourseName: values.academicCourseName ?? null,
      completionDate: values.completionDate ?? null,
      isStudent: values.isStudent,
      isEmployee: values.isEmployee,
      isTeachingStaff: values.isTeachingStaff,
      isMonitor: values.isMonitor,
      isPCD: values.isPCD,
      pcdDescription: values.pcdDescription ?? null,
      useAccessibilityTool: values.useAccessibilityTool,
      accessibilityToolDescription: values.accessibilityToolDescription ?? null,
      personalAddress: mapAddress(values.personalAddress),
      paymentAddress: mapAddress(values.paymentAddress),
    } as CreatePersonRequestViewModel);
  };

  const errorsByStep = useMemo(() => countErrorsByStep(errors), [errors]);
  const totalErrors = Object.values(errorsByStep).reduce((a, b) => a + b, 0);
  const showValidationAlert = isSubmitted && totalErrors > 0;

  const tabProps = { control, register, errors };
  const currentKey: StepKey = StepKeys[currentStep] ?? 'personal';
  const isLastStep = currentStep === StepKeys.length - 1;

  const stepperSteps = useMemo(
    () =>
      StepKeys.map(key => ({
        key,
        title: t(`personForm.steps.${key}.title`),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, StepKeys.map],
  );

  const handleCancelClick = useCallback(() => {
    if (isDirty || rolesIsDirty) {
      setShowUnsavedModal(true);
    } else {
      onCancel();
    }
  }, [isDirty, rolesIsDirty, onCancel]);

  /**
   * Dispara o submit programaticamente via onClick em vez de type="submit".
   * Evita que a reconciliação React (mudança de type "button"→"submit" no mesmo
   * nó DOM durante async handleNext) dispare o form submit involuntariamente.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: onFormSubmit é estável por ser definida no mesmo render cycle que handleSave; extraí-la para useCallback exigiria listar todas as suas deps externas
  const handleSave = useCallback(() => {
    void handleSubmit(onFormSubmit)();
  }, [handleSubmit, onFormSubmit]);

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="flex flex-col gap-xxs">
      {person && <PersonFormHeader person={person} />}

      {serverError && (
        <Alert type="danger">
          <p className="ts-paragraph-xs">{serverError}</p>
        </Alert>
      )}

      {showValidationAlert && (
        <Alert type="danger">
          <div className="flex w-full flex-col gap-xxs">
            <div className="flex flex-col gap-quark">
              <p className="ts-heading-xxs">{t('personForm.wizard.validationTitle')}</p>
              <p className="ts-caption-xxs">{t('personForm.wizard.validationDescription')}</p>
            </div>
            <div className="flex flex-wrap gap-quark">
              {StepKeys.map((key, i) => {
                const count = errorsByStep[key];
                if (count === 0) return null;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => goToStep(i)}
                    className="rounded-pill border-hairline border-base-danger-300 bg-base-danger-100 px-nano py-quark ts-caption-xxs text-base-danger-700 transition-opacity hover:opacity-intense focus:outline-none focus:ring-[0.25rem] focus:ring-ctx-highlight-ringFocusShadow"
                  >
                    {t(`personForm.steps.${key}.title`)} ·{' '}
                    {t('personForm.wizard.stepHasErrors', { count })}
                  </button>
                );
              })}
            </div>
          </div>
        </Alert>
      )}

      {person ? (
        <PersonFormTabs
          steps={stepperSteps}
          currentStep={currentStep}
          errorCountByStep={errorsByStep}
          isSubmitted={isSubmitted}
          onStepClick={index => void goToStep(index)}
        />
      ) : (
        <PersonFormStepper
          steps={stepperSteps}
          currentStep={currentStep}
          errorCountByStep={errorsByStep}
          isSubmitted={isSubmitted}
          maxReachedStep={maxReachedStep}
          onStepClick={goToStep}
        />
      )}

      <section
        aria-labelledby="person-form-step-title"
        className="@container flex flex-col gap-xxs rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xs"
      >
        <header className="flex items-start justify-between gap-xs">
          <div className="flex flex-col gap-quark">
            <p className="ts-caption-xxs uppercase tracking-wide text-ctx-content-title">
              {t('personForm.wizard.stepCounter', {
                current: currentStep + 1,
                total: StepKeys.length,
              })}
            </p>
            <h2 id="person-form-step-title" className="ts-heading-md text-ctx-content-title">
              {t(`personForm.steps.${currentKey}.title`)}
            </h2>
            <p className="ts-paragraph-xs text-ctx-content-base">
              {t(`personForm.steps.${currentKey}.description`)}
            </p>
          </div>
          {errorsByStep[currentKey] > 0 && isSubmitted && (
            <Tag
              size="sm"
              status="negative"
              label={t('personForm.wizard.stepHasErrors', {
                count: errorsByStep[currentKey],
              })}
              custom={{ text: 'text-nowrap' }}
            />
          )}
        </header>

        <div className="border-t-hairline border-ctx-layout-border pt-xxs">
          {currentKey === 'personal' && <PersonalTab {...tabProps} setValue={setValue} />}
          {currentKey === 'contact' && <AdditionalInfoTab {...tabProps} />}
          {currentKey === 'professional' && <ProfessionalTab {...tabProps} setValue={setValue} />}
          {currentKey === 'documents' && <DocumentsTab personId={person?.id} />}
          {currentKey === 'addresses' && (
            <AddressesTab control={control} person={person} setValue={setValue} />
          )}
          {currentKey === 'profiles' && (
            <ProfilesTab control={control} register={register} isLoading={isLoadingProfiles} />
          )}
          {currentKey === 'financial' && (
            <FinancialTab {...tabProps} isLoading={isLoadingFinancial} />
          )}
          {currentKey === 'roles' && (
            <RolesTab
              personId={person?.id}
              saveRef={rolesSaveRef}
              onDirtyChange={setRolesIsDirty}
            />
          )}
        </div>
      </section>

      <UnsavedChangesModal
        open={showUnsavedModal}
        onLeave={() => {
          setShowUnsavedModal(false);
          onCancel();
        }}
        onCancel={() => setShowUnsavedModal(false)}
      />

      <PersonFormActionBar
        currentStep={currentStep}
        isLastStep={isLastStep}
        isEditMode={!!person}
        isSubmitting={isSubmitting}
        stepSaveStatus={stepSaveStatus}
        stepSaveError={stepSaveError}
        onCancelClick={handleCancelClick}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onSaveRoles={handleSaveRolesStep}
        onSave={handleSave}
      />
    </form>
  );
}
